import { Link } from "react-router-dom";
import { Building2, ArrowRight, ShieldCheck, MapPin, Stethoscope, Languages } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath } from "../config/pages";
import { PageHero, CTABand } from "../components/Sections";
import { Reveal } from "../components/motion/Reveal";
import { webPageSchema, breadcrumbSchema } from "../lib/schema";

const META = {
  title: {
    en: "Dental Clinics in Vietnam | Selected Providers | Dentel Vietnam",
    fr: "Cliniques dentaires au Vietnam | Prestataires sélectionnés | Dentel Vietnam",
    de: "Zahnkliniken in Vietnam | Ausgewählte Anbieter | Dentel Vietnam",
  },
  desc: {
    en: "Verified clinic profiles will appear here as they are confirmed. We only list real, licensed providers — no invented listings.",
    fr: "Les profils de cliniques vérifiés apparaîtront ici au fur et à mesure. Nous ne listons que des prestataires réels et agréés — aucune fiche inventée.",
    de: "Verifizierte Klinikprofile erscheinen hier, sobald sie bestätigt sind. Wir listen nur echte, lizenzierte Anbieter — keine erfundenen Einträge.",
  },
  h1: { en: "Dental Clinics in Vietnam", fr: "Cliniques dentaires au Vietnam", de: "Zahnkliniken in Vietnam" },
  intro: {
    en: "We are building a directory of verified, licensed dental providers. To protect trust, we do not list clinics until their details are confirmed.",
    fr: "Nous constituons un annuaire de prestataires dentaires agréés et vérifiés. Par souci de confiance, nous ne listons pas de cliniques avant confirmation de leurs informations.",
    de: "Wir bauen ein Verzeichnis verifizierter, lizenzierter Zahnanbieter auf. Aus Vertrauensgründen listen wir Kliniken erst nach Bestätigung ihrer Angaben.",
  },
  emptyTitle: {
    en: "Verified clinic profiles coming soon",
    fr: "Profils de cliniques vérifiés à venir",
    de: "Verifizierte Klinikprofile in Kürze",
  },
  emptyBody: {
    en: "In the meantime, tell us what you need and we will help you connect with a selected licensed provider suited to your case.",
    fr: "En attendant, dites-nous ce dont vous avez besoin et nous vous aiderons à contacter un prestataire agréé adapté à votre cas.",
    de: "Sagen Sie uns in der Zwischenzeit, was Sie benötigen — wir helfen Ihnen, einen passenden lizenzierten Anbieter zu finden.",
  },
};

const FEATURES = [
  { icon: ShieldCheck, key: "licence" },
  { icon: Stethoscope, key: "doctors" },
  { icon: MapPin, key: "map" },
  { icon: Languages, key: "languages" },
];

export default function Clinics() {
  const { t, lang, p } = useI18n();
  const alternates = { en: pagePath("clinics", "en"), fr: pagePath("clinics", "fr"), de: pagePath("clinics", "de") };
  useRegisterAlternates(alternates);
  const path = pagePath("clinics", lang);
  const title = META.h1[lang] || META.h1.en;

  const featureLabels = t.trust.checklist;

  return (
    <>
      <Seo
        title={META.title[lang] || META.title.en}
        description={META.desc[lang] || META.desc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        jsonLd={[
          webPageSchema({ name: title, description: META.desc[lang] || META.desc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, { name: title, path }]),
        ]}
      />
      <PageHero eyebrow={t.nav.clinics} title={title} intro={META.intro[lang] || META.intro.en} crumbs={[{ name: t.nav.clinics, to: path }]} />

      <section className="container-dv py-16 lg:py-20">
        <Reveal>
          <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-10 text-center shadow-soft" data-testid="clinics-empty">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-dental/8 text-dental">
              <Building2 className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl text-navy">{META.emptyTitle[lang] || META.emptyTitle.en}</h2>
            <p className="mx-auto mt-3 max-w-xl text-navy/65 leading-relaxed">{META.emptyBody[lang] || META.emptyBody.en}</p>
            <Link to={p("contact")} data-testid="clinics-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-dental px-7 py-4 text-base font-semibold text-white hover:bg-dental-dark transition-colors">
              {t.cta.myFreePlan}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-sm font-semibold uppercase tracking-[0.18em] text-navy/45">{t.common.whatToCheck}</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="rounded-2xl border border-navy/8 bg-white p-6 shadow-soft">
                  <Icon className="h-6 w-6 text-dental" />
                  <p className="mt-3 font-semibold text-navy">{featureLabels[i]}</p>
                </div>
              );
            })}
          </div>
        </Reveal>
      </section>

      <CTABand />
    </>
  );
}
